import QuickBooksEditor from './QuickBooksEditor';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, CheckCircle, XCircle, Clock, Eye, Trash2, Download, ArrowLeft } from 'lucide-react';

const QuickBooksBatchPage = ({ 
  files, 
  batchData: externalBatchData,
  setBatchData: setExternalBatchData,
  user, 
  extractInvoiceBackend, 
  logTransaction,
  onBack
}) => {
  const processingStartedRef = useRef(false);
  const batchIdRef = useRef(null);
  const mountedRef = useRef(true);

  const batchData = externalBatchData;
  const setBatchData = setExternalBatchData;

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showJson, setShowJson] = useState(false);

useEffect(() => {

    mountedRef.current = true;
  // Generate unique batch ID
   if (batchData.length === 0 && files.length > 0) {
  const currentBatchId = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  batchIdRef.current = currentBatchId;
  processingStartedRef.current = false;
  
 const initBatch = files.map((file, index) => {
    // Check if URL already exists in batchData
    const existingItem = batchData.find(item => item.fileName === file.name);
    return {
      id: `${currentBatchId}_${index}`,
      file: file,
      fileName: file.name,
      status: 'pending',
      pdfUrl: existingItem?.pdfUrl || URL.createObjectURL(file),
      invoiceData: null,
      error: null,
      batchId: currentBatchId
    };
  });
  
  setBatchData(initBatch);
  
  // Start processing only if not already started
  if (!processingStartedRef.current) {
    processingStartedRef.current = true;
    processAllFiles(initBatch, currentBatchId);
  }
   }else if (batchData.length > 0 && files.length === 0) {
    // If batchData exists but no files, user removed all - clear and exit
    setBatchData([]);
    onBack();
  }
  return () => {
    mountedRef.current = false;
  };

}
, [files, batchData.length, setBatchData]);
// Warning on page refresh/close
useEffect(() => {
  const handleBeforeUnload = (e) => {
    if (batchData.length > 0) {
      e.preventDefault();
      e.returnValue = '';
      return '';
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}, [batchData.length]);

useEffect(() => {
  if (!user) {
    setBatchData([]);
    onBack();
  }
}, [user, setBatchData, onBack]);

const processAllFiles = useCallback(async (initialData, batchId) => {
  // Prevent duplicate processing
  if (batchId !== batchIdRef.current) {
    console.log('Stale batch detected, skipping processing');
    return;
  }
  
  for (let i = 0; i < initialData.length; i++) {
    // Check if component is still mounted and batch is still current
    if (!mountedRef.current || batchId !== batchIdRef.current) {
      console.log('Processing cancelled: component unmounted or new batch started');
      break;
    }
    await processFile(i, initialData, batchId);
  }
}, []);


const processFile = async (index, dataArray = null, batchId = null) => {
  // Safety check: ensure we're processing the current batch
  if (batchId && batchId !== batchIdRef.current) {
    console.log(`Skipping file ${index}: stale batch`);
    return;
  }
  
  const currentData = dataArray || batchData;
  
  // Check if already processed/processing
  if (currentData[index]?.status === 'completed' || currentData[index]?.status === 'processing') {
    console.log(`Skipping file ${index}: already ${currentData[index].status}`);
    return;
  }
  
  if (!mountedRef.current) return;
  
  setBatchData(prev => prev.map((item, idx) => 
    idx === index ? { ...item, status: 'processing' } : item
  ));

  try {
    const file = currentData[index]?.file || files[index];
    
    const reader = new FileReader();
    const base64Promise = new Promise((resolve, reject) => {
      reader.onload = (event) => resolve(event.target.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const base64 = await base64Promise;
    
    // Check again before API call
    if (!mountedRef.current || (batchId && batchId !== batchIdRef.current)) {
      return;
    }
    
    const extractedData = await extractInvoiceBackend(base64);

    if (!mountedRef.current) return;

    setBatchData(prev => prev.map((item, idx) => 
      idx === index ? { 
        ...item, 
        status: 'completed',
        invoiceData: extractedData,
        error: null
      } : item
    ));

    if (extractedData['invoice-number']) {
      await logTransaction(extractedData['invoice-number'], file.name);
    }

  } catch (error) {
    if (!mountedRef.current) return;
    
    setBatchData(prev => prev.map((item, idx) => 
      idx === index ? { 
        ...item, 
        status: 'error',
        error: error.message
      } : item
    ));
  }
};

 const retryFile = async (index) => {
  await processFile(index, null, batchIdRef.current);
};

const removeFile = (index) => {
  const fileName = batchData[index]?.fileName || 'this file';
  if (window.confirm(`Are you sure you want to remove "${fileName}"?`)) {
    const updatedData = batchData.filter((_, idx) => idx !== index);
    setBatchData(updatedData);
    
    if (selectedIndex === index) {
      setSelectedIndex(null);
    }
    
    // If all files removed, go back to upload page
    if (updatedData.length === 0) {
      onBack();
    }
  }
};
  const updateInvoiceData = (index, newData) => {
    setBatchData(prev => prev.map((item, idx) => 
      idx === index ? { ...item, invoiceData: newData } : item
    ));
  };

  const handleBack = () => {
  if (batchData.length > 0 && batchData.some(item => item.status === 'processing' || item.status === 'completed')) {
    const confirmMsg = 'Go back to main screen? Your batch data will be saved and you can return to it.';
    if (window.confirm(confirmMsg)) {
      onBack();
    }
  } else {
    onBack();
  }
};

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-5 h-5 text-gray-400" />;
      case 'processing': return <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Waiting...';
      case 'processing': return 'Processing...';
      case 'completed': return 'Completed';
      case 'error': return 'Failed';
      default: return '';
    }
  };

  const completedCount = batchData.filter(item => item.status === 'completed').length;
  const errorCount = batchData.filter(item => item.status === 'error').length;
  const processingCount = batchData.filter(item => item.status === 'processing').length;
const exportAllQuickBooksCSV = () => {
  const completedInvoices = batchData.filter(item => item.status === 'completed' && item.invoiceData);
  
  if (completedInvoices.length === 0) {
    alert('No completed invoices to export');
    return;
  }

  const escapeCSV = (value) => {
    if (value == null || value === '') return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  let csv = '';
  
  // QuickBooks CSV Header (same as your single export)
  csv += '*InvoiceNo,*Customer,CustomerEmail,BillingAddress,BillingCity,BillingState,BillingZip,BillingCountry,*InvoiceDate,*DueDate,Terms,Location,Memo,*Item(Product/Service),ItemDescription,ItemQuantity,ItemRate,*ItemAmount,Taxable,Service Date\n';
  
  completedInvoices.forEach(item => {
    const inv = item.invoiceData;
    
    const invoiceNo = escapeCSV(inv['invoice-number']);
    const customer = escapeCSV(inv.customer.name || inv.customer.number || 'Customer');
    const customerEmail = escapeCSV(inv.customer.email || inv['requester-email']);
    const billingAddress = escapeCSV(inv.customer.address);
    const billingCity = escapeCSV(inv.customer.city);
    const billingState = escapeCSV(inv.customer.state);
    const billingZip = escapeCSV(inv.customer.zip);
    const billingCountry = escapeCSV(inv.customer.country);
    
    const invoiceDate = escapeCSV(inv['invoice-date']?.split(' ')[0] || '');
    
    // Calculate default due date (30 days) if blank
    let dueDate = inv['due-date']?.split(' ')[0] || '';
    if (!dueDate && inv['invoice-date']) {
      const invoiceDateObj = new Date(inv['invoice-date']);
      if (!isNaN(invoiceDateObj.getTime())) {
        const defaultDueDate = new Date(invoiceDateObj);
        defaultDueDate.setDate(defaultDueDate.getDate() + 30);
        dueDate = defaultDueDate.toLocaleDateString('en-US', { 
          month: '2-digit', 
          day: '2-digit', 
          year: 'numeric' 
        });
      }
    }
    dueDate = escapeCSV(dueDate);
    
    const terms = escapeCSV(inv['payment-terms']);
    const location = '';
    const memo = '';
    
    // Charge amounts
    const shippingAmount = (inv['shipping-amount'] && inv['shipping-amount'] !== '') ? parseFloat(inv['shipping-amount']) : 0;
    const handlingAmount = (inv['handling-amount'] && inv['handling-amount'] !== '') ? parseFloat(inv['handling-amount']) : 0;
    const miscAmount = (inv['misc-amount'] && inv['misc-amount'] !== '') ? parseFloat(inv['misc-amount']) : 0;
    const discountAmount = (inv['discount-amount'] && inv['discount-amount'] !== '') ? Math.abs(parseFloat(inv['discount-amount'])) : 0;
    
    // Add regular line items
    inv['invoice-lines'].forEach((line, index) => {
      const itemName = escapeCSV(line['item-type'] || 'Service');
      const itemDesc = escapeCSV(line.description || line['supplier-part-number'] || '');
      const itemQty = escapeCSV(line.quantity || '1');
      const itemRate = escapeCSV(line.price || '0');
      
      const lineTotal = (parseFloat(line.quantity || 0) * parseFloat(line.price || 0)).toFixed(2);
      const itemAmount = escapeCSV(line['custom-fields']['invoice-line-total'] || lineTotal);
      const serviceDate = '';
      
      if (index === 0) {
        csv += `${invoiceNo},${customer},${customerEmail},${billingAddress},${billingCity},${billingState},${billingZip},${billingCountry},${invoiceDate},${dueDate},${terms},${location},${memo},${itemName},${itemDesc},${itemQty},${itemRate},${itemAmount},N,${serviceDate}\n`;
      } else {
        csv += `${invoiceNo},,,,,,,,,,,,,${itemName},${itemDesc},${itemQty},${itemRate},${itemAmount},N,${serviceDate}\n`;
      }
    });
    
    // Add shipping
    if (shippingAmount > 0) {
      csv += `${invoiceNo},,,,,,,,,,,,,Shipping,Shipping,1,${shippingAmount.toFixed(2)},${shippingAmount.toFixed(2)},N,\n`;
    }
    
    // Add handling
    if (handlingAmount > 0) {
      csv += `${invoiceNo},,,,,,,,,,,,,Handling,Handling,1,${handlingAmount.toFixed(2)},${handlingAmount.toFixed(2)},N,\n`;
    }
    
    // Add misc
    if (miscAmount > 0) {
      csv += `${invoiceNo},,,,,,,,,,,,,Misc Charges,Misc Charges,1,${miscAmount.toFixed(2)},${miscAmount.toFixed(2)},N,\n`;
    }
    
    // Add discount (negative)
    if (discountAmount > 0) {
      const negativeDiscount = -Math.abs(discountAmount);
      csv += `${invoiceNo},,,,,,,,,,,,,Discount,Discount,1,${negativeDiscount.toFixed(2)},${negativeDiscount.toFixed(2)},N,\n`;
    }
      // Add header-level taxes as separate line items if they exist
  if (inv['tax-lines'] && inv['tax-lines'].length > 0) {
    inv['tax-lines'].forEach((tax) => {
      const taxAmt = parseFloat(tax.amount || 0).toFixed(2);
      const taxDesc = escapeCSV(tax.description || 'Tax');
      csv += `${invoiceNo},,,,,,,,,,,,,Service,${taxDesc},1,${taxAmt},${taxAmt},N,\n`;
    });
  }
  });

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `quickbooks_batch_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

  // Detail view for single file
  if (selectedIndex !== null && batchData[selectedIndex]) {
    const selectedItem = batchData[selectedIndex];
    
    return (
      <div className="space-y-2">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-3">
          <button
            onClick={() => setSelectedIndex(null)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Batch List
          </button>
          <div className="mt-2 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">{selectedItem.fileName}</p>
              <p className="text-xs text-gray-500">File {selectedIndex + 1} of {batchData.length}</p>
            </div>
            <div className="flex items-center gap-2">
              {selectedIndex > 0 && (
                <button
                  onClick={() => setSelectedIndex(selectedIndex - 1)}
                  className="px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                >
                  ← Previous
                </button>
              )}
              {selectedIndex < batchData.length - 1 && (
                <button
                  onClick={() => setSelectedIndex(selectedIndex + 1)}
                  className="px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                >
                  Next →
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Invoice Preview</h3>
            <div className="bg-gray-100 rounded-lg h-[calc(100vh-180px)] overflow-hidden">
              <iframe 
                src={selectedItem.pdfUrl} 
                className="w-full h-full rounded-lg border-0"
                title="Invoice Preview"
              />
            </div>
          </div>

          <div className="space-y-2">
           <QuickBooksEditor
                           key={selectedIndex}
                           invoiceData={selectedItem.invoiceData}
                           setInvoiceData={(newData) => updateInvoiceData(selectedIndex, newData)}
                           pdfFile={selectedItem.file}
                           showJson={showJson}
                           setShowJson={setShowJson}
                         />
          </div>
        </div>
      </div>
    );
  }

  // Batch list view
  return (
    <div className="space-y-2">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <span className="text-lg">💚</span>
              QuickBooks Batch Processing
            </h2>
            <div className="flex items-center gap-3 text-xs">
              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded font-medium">
                Total: {batchData.length}
              </span>
              <span className="px-2 py-1 bg-green-50 text-green-700 rounded font-medium">
                ✓ {completedCount}
              </span>
              <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded font-medium">
                ⏳ {processingCount}
              </span>
              <span className="px-2 py-1 bg-red-50 text-red-700 rounded font-medium">
                ✗ {errorCount}
              </span>
            </div>
          </div>
          
          {completedCount > 0 && (
            <button
              onClick={exportAllQuickBooksCSV}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              <Download className="w-4 h-4" />
              Export All as CSV ({completedCount})
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Files</h3>
        
        <div className="space-y-3">
          {batchData.map((item, index) => (
            <div 
              key={item.id} 
              className="border border-gray-200 rounded-lg p-2 hover:border-green-300 transition-colors"
            >
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 mt-1">
                  {getStatusIcon(item.status)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">
                      {item.fileName}
                    </h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      item.status === 'completed' ? 'bg-green-100 text-green-700' :
                      item.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                      item.status === 'error' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {getStatusText(item.status)}
                    </span>
                  </div>

                 {item.status === 'completed' && item.invoiceData && (
  <div className="flex items-center justify-between gap-2 p-2 bg-green-50 rounded border border-green-200 text-xs">
    <div className="flex items-center gap-4 flex-1 min-w-0">
      <span className="text-green-700 font-medium">Invoice:</span>
      <span className="text-gray-900 font-semibold">{item.invoiceData['invoice-number'] || 'N/A'}</span>
      
      <span className="text-green-700 font-medium">Customer:</span>
      <span className="text-gray-900 font-semibold truncate max-w-[150px]" title={item.invoiceData.customer?.name || 'N/A'}>
        {item.invoiceData.customer?.name || 'N/A'}
      </span>
      
      <span className="text-green-700 font-medium">Amount:</span>
      <span className="text-gray-900 font-semibold">
        {item.invoiceData.currency?.code || 'USD'} {item.invoiceData['custom-fields']?.['invoice-total'] || '0.00'}
      </span>
      
      <span className="text-green-700 font-medium">Date:</span>
      <span className="text-gray-900 font-semibold">{item.invoiceData['invoice-date'] || 'N/A'}</span>
    </div>
    
    <div className="flex items-center gap-2 flex-shrink-0">
      <button
        onClick={() => setSelectedIndex(index)}
        className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
      >
        <Eye className="w-3 h-3" />
        View
      </button>
      <button
        onClick={() => removeFile(index)}
        className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
      >
        <Trash2 className="w-3 h-3" />
        Remove
      </button>
    </div>
  </div>
)}

{item.status === 'error' && item.error && (
  <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
    Error: {item.error}
  </div>
)}

<div className="flex items-center gap-2">
  {item.status === 'error' && (
    <button
      onClick={() => retryFile(index)}
      className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
    >
      <Loader2 className="w-3 h-3" />
      Retry
    </button>
  )}
</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuickBooksBatchPage;